import React from 'react';
import { Formik, useFormikContext } from 'formik';

import InstructionsMarkdownEditorFormik from '~/components/Admin/AiExperts/InstructionsMarkdownEditorFormik';
import CardDialog from '~/components/CardDialog';
import AlertBanner from '~/components/core/AlertBanner';
import DialogFooterActions from '~/components/core/DialogFooterActions';
import Text from '~/components/core/TextComponents/Text';
import TextFieldFormik from '~/components/TextFieldFormik';

import copies from '../copies';
import type { ConductorStageRow, StageDialogFormValues } from '../types';

import isStageConfigured from './isStageConfigured';

interface StageDialogProps {
  stage: ConductorStageRow;
  onClose: () => void;
  /** Persists the stage and resolves to the refreshed scope, or `undefined` if the refresh failed. */
  onSave: (values: StageDialogFormValues) => Promise<unknown>;
}

/**
 * Warns while the draft is missing a goal or instructions: a stage needs both to be configured.
 *
 * Reads the live form values rather than the saved stage, so it appears and disappears as the
 * operator types. It must never become a blocking validation: "not configured" is a
 * legitimate state that saves, and this alert plus the list's chip are the only things that
 * make choosing it deliberate rather than accidental.
 */
const NotConfiguredAlert: React.FC = () => {
  const { values } = useFormikContext<StageDialogFormValues>();
  if (isStageConfigured(values)) {
    return null;
  }
  return <AlertBanner alertType="warning" note={copies.stageDialog.notConfiguredWarning} withIcon />;
};

/**
 * Holds a local draft committed on Save, so Cancel discards.
 *
 * The dialog it replaces was bound straight to the stored value with a single "Done" button —
 * there was nothing to save and nothing to cancel. Under per-part save that had to change, or
 * Cancel would be a lie.
 *
 * Keyed on stage **key** by its parent, never on position: position is no longer identity.
 */
const StageDialog: React.FC<StageDialogProps> = ({ stage, onClose, onSave }) => {
  const initialValues: StageDialogFormValues = {
    goal: stage.content?.goal ?? '',
    instructions: stage.content?.instructions ?? '',
  };

  const handleSubmit = async (values: StageDialogFormValues) => {
    const saved = await onSave(values);
    if (saved !== undefined) {
      onClose();
    }
  };

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {({ isSubmitting, handleSubmit: submitForm }) => (
        <CardDialog
          isDialog
          open
          maxWidth="md"
          fullWidth
          title={copies.stageDialog.title(stage.label)}
          onClose={onClose}
          preventClose={isSubmitting}
          footerActions={
            <DialogFooterActions
              onClickPrimary={() => submitForm()}
              onClickSecondary={onClose}
              disabled={isSubmitting}
            />
          }
        >
          <div className="flex flex-col gap-16">
            {/* Read-only facts, not fields: the name, key and order come from the claim-stage
                configuration and are not editable here. Rendered as text rather than as disabled
                inputs, which would imply there is a form value behind them. */}
            <div className="flex gap-32">
              <div className="flex flex-col gap-4">
                <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                  {copies.stageDialog.nameLabel}
                </Text>
                <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                  {stage.label}
                </Text>
              </div>
              <div className="flex flex-col gap-4">
                <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                  {copies.stageDialog.keyLabel}
                </Text>
                <Text variant={Text.VARIANTS.SM}>{stage.key}</Text>
              </div>
            </div>
            <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
              {copies.stageDialog.derivedHelper}
            </Text>
            <NotConfiguredAlert />
            <div className="flex flex-col gap-4">
              <TextFieldFormik
                id="goal"
                label={copies.stageDialog.goalLabel}
                placeholder={copies.stageDialog.goalPlaceholder}
                fullWidth
              />
              <Text variant={Text.VARIANTS.XS} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
                {copies.stageDialog.goalHelper}
              </Text>
            </div>
            <div className="flex flex-col gap-8">
              <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.SEMI_BOLD}>
                {copies.stageDialog.instructionsLabel}
              </Text>
              <InstructionsMarkdownEditorFormik id="instructions" />
            </div>
          </div>
        </CardDialog>
      )}
    </Formik>
  );
};

export default StageDialog;
